import type { TenantOverviewDTO } from '@liks/lac-svc-api/esm/models/index';
import type { LacTenantOverviewDateRange } from '../../types/lac';
import {
  dayToIsoRange,
  formatCalendarDay,
  lastDayOfMonth,
  toCalendarDay,
} from '../../utils/dateTimeUtils';

/**
 * Inclusive overview bounds for one calendar month.
 * Current month is month-to-date (1st → today); past months use the full month.
 * @param month 1–12
 */
export function getMonthOverviewDateRange(
  year: number,
  month: number,
  now: Date = new Date(),
): LacTenantOverviewDateRange {
  const fromDay = formatCalendarDay(year, month, 1);
  const isCurrentMonth =
    year === now.getFullYear() && month === now.getMonth() + 1;
  const toDay = isCurrentMonth
    ? toCalendarDay(now)
    : formatCalendarDay(year, month, lastDayOfMonth(year, month));
  return dayToIsoRange(fromDay, toDay);
}

/**
 * Inclusive overview bounds for one calendar year.
 * Current year is year-to-date (Jan 1 → today); past years use Jan 1 → Dec 31.
 */
export function getYearOverviewDateRange(
  year: number,
  now: Date = new Date(),
): LacTenantOverviewDateRange {
  const fromDay = formatCalendarDay(year, 1, 1);
  const isCurrentYear = year === now.getFullYear();
  const toDay = isCurrentYear
    ? toCalendarDay(now)
    : formatCalendarDay(year, 12, 31);
  return dayToIsoRange(fromDay, toDay);
}

/**
 * Calendar month containing `now`, as ISO-8601 datetime bounds for analytics filters.
 * `fromDate` is local midnight on the 1st; `toDate` is local end of today
 * (stable query key within a day).
 */
export function getCurrentMonthDateRange(
  now: Date = new Date(),
): LacTenantOverviewDateRange {
  return getMonthOverviewDateRange(now.getFullYear(), now.getMonth() + 1, now);
}

/**
 * Calendar year containing `now`, as ISO-8601 datetime bounds for analytics filters.
 * `fromDate` is local midnight on Jan 1; `toDate` is local end of today
 * (stable query key within a day).
 */
export function getCurrentYearDateRange(
  now: Date = new Date(),
): LacTenantOverviewDateRange {
  return getYearOverviewDateRange(now.getFullYear(), now);
}

/** Sum `tokenData.estimatedCosts` across tenant overviews (same field as org rows). */
export function sumTenantOverviewEstimatedCosts(
  overviews: TenantOverviewDTO[] | null | undefined,
): number {
  let total = 0;
  for (const overview of overviews ?? []) {
    const estimatedCosts = overview.tokenData?.estimatedCosts;
    if (estimatedCosts != null && Number.isFinite(estimatedCosts)) {
      total += estimatedCosts;
    }
  }
  return total;
}

function sumCounts(
  items: Array<{ count?: number | null }> | null | undefined,
): number {
  if (!items) {
    return 0;
  }

  return items.reduce((total, item) => total + (item.count ?? 0), 0);
}

export function getTenantOverviewCrawlJobCount(
  overview: TenantOverviewDTO | null | undefined,
): number {
  return sumCounts(overview?.crawlJobMetrics?.statusCounts);
}

export function getTenantOverviewChatbotMembershipCount(
  overview: TenantOverviewDTO | null | undefined,
): number {
  return sumCounts(overview?.chatbotMembershipMetrics?.roleCounts);
}
